import React from 'react';
import { Box, Chip } from '@mui/material';
import { listDayChoices } from './missedCallFormat';

/**
 * Which day's list rows go under (plan §3.9): 오늘, 어제 and the days before it, as chips. Shared by the desktop
 * upload, the phone page and 번호로 추가.
 *
 * Single-select (the default, used by 번호로 추가): `value` is a date, or null meaning today, and onChange gives
 * the same back.
 *
 * `multiple` (the screenshot uploads): `value` is an array of dates and onChange gives an array. One screenshot
 * run can show several days, so the uploader ticks the ones to keep and the rest are dropped. Today starts
 * ticked, and the last ticked chip cannot be un-ticked — an empty selection would throw every row away.
 */
const DayChoice = ({ today, pastDays = 6, value, onChange, disabled = false, large = false, multiple = false }) => {
    const selected = multiple
        ? new Set((Array.isArray(value) && value.length ? value : [today]).map(String))
        : new Set([String(value || today)]);

    const toggle = (date) => {
        if (!multiple) {
            onChange(date === today ? null : date);
            return;
        }
        const next = new Set(selected);
        if (next.has(date)) {
            if (next.size === 1) return;   // never nothing: that would drop every row
            next.delete(date);
        } else {
            next.add(date);
        }
        onChange([...next].sort());
    };

    return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
            {listDayChoices(today, pastDays ?? 6).map((c) => {
                const picked = selected.has(c.date);
                return (
                    <Chip
                        key={c.date}
                        label={c.label}
                        clickable
                        disabled={disabled}
                        color={picked ? 'primary' : 'default'}
                        variant={picked ? 'filled' : 'outlined'}
                        aria-pressed={picked}
                        onClick={() => toggle(c.date)}
                        sx={large ? { fontSize: 15, height: 36 } : undefined}
                    />
                );
            })}
        </Box>
    );
};

export default DayChoice;
